import { useLazyQuery, useSubscription } from '@apollo/client'
import {
  CircularProgress,
  Hidden,
  IconButton,
  MenuItem,
  Typography
} from '@material-ui/core'
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded'
import CopyIcon from '@material-ui/icons/FileCopy'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  jobsListVar,
  updateJobStatusForAccessManagement,
  updateJobStatusForCreateRateplan
} from '../../cache/jobs'
import { TRACK_JOB_QUERY } from '../../graphql/jobTracking'
import { RATE_PLAN_GENERATE_JOB_HANDLER_SUBSCRIPTION } from '../../graphql/rateplanGenerationSubscription'
import { ACCESS_MANAGEMENT_JOB_HANDLER_SUBSCRIPTION } from '../../graphql/userManagement'
import { gregorianToJalaali } from '../../helpers/date'
import { lang } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import { JobStatueAndDetails, Status } from '../../types/BulkAccessManagement'
import { JobTrackingResponse } from '../../types/jobTracking'
import { JobStatusForRateplan } from '../../types/rateplan'
import JobDetailsModal from './JobDetailsModal'
import StatusBox from './StatusBox'
import useStyles from './styles/table.styles'

interface JobRowProps {
  data: JobsType
  onChangeJobStatus: (status: Status) => void
  jobIndex: number
}

const JobsRow: React.FC<JobRowProps> = ({
  data,
  onChangeJobStatus,
  jobIndex
}) => {
  const {
    trackID,
    creationDate,
    status,
    subjobs,
    subjobsForGenerateRatePlan,
    hasDetails
  } = data
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)

  const [trackJob, { refetch }] = useLazyQuery<JobTrackingResponse>(
    TRACK_JOB_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        if (!res.trackJob.status.includes('Finished')) {
          setTimeout(() => {
            refetch()
          }, 20000)
        } else {
          onChangeJobStatus(res.trackJob.status)
        }
      }
    }
  )

  useEffect(() => {
    if (!data.hasDetails && !status.includes('Finished')) {
      trackJob({
        variables: {
          trackingID: trackID
        }
      })
    }
  }, [jobIndex])

  const handleDeleteJob = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation()
    if (status.includes('Finished')) {
      const currentJobsList = jobsListVar()
      const newJobsList = [
        ...currentJobsList.slice(0, jobIndex),
        ...currentJobsList.slice(jobIndex + 1)
      ]
      jobsListVar(newJobsList)
      localStorage.setItem('jobs', JSON.stringify(newJobsList))
    }
  }

  const {} = useSubscription<{
    AccessManagementJobHandler: JobStatueAndDetails
  }>(ACCESS_MANAGEMENT_JOB_HANDLER_SUBSCRIPTION, {
    variables: {
      trackingID: data.trackID
    },
    onData: (d) => {
      if (d.data.data) {
        updateJobStatusForAccessManagement(
          jobIndex,
          d.data.data.AccessManagementJobHandler.status as Status,
          d.data.data.AccessManagementJobHandler.email
        )
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
    shouldResubscribe: true,
    skip:
      !data.forBulkAccess ||
      !data.trackID ||
      !data.hasDetails ||
      data.status === 'FinishedSuccessfully'
  })

  // rateplan generation
  const {} = useSubscription<{
    MultipleRatePlanJobHandler: JobStatusForRateplan
  }>(RATE_PLAN_GENERATE_JOB_HANDLER_SUBSCRIPTION, {
    variables: {
      trackingID: data.trackID
    },
    onData: (d) => {
      if (d.data.data) {
        updateJobStatusForCreateRateplan(
          jobIndex,
          d.data.data.MultipleRatePlanJobHandler.status as Status,
          d.data.data.MultipleRatePlanJobHandler.code
        )
      }
    },
    shouldResubscribe: true,
    skip:
      !data.forRatePlan ||
      !data.trackID ||
      !data.hasDetails ||
      data.status === 'FinishedSuccessfully'
  })

  const handleClickOnEntireRow = () => {
    if (hasDetails) {
      setIsOpen(true)
    }
  }

  const onClickToCopy = () => {
    navigator.clipboard.writeText(trackID)
  }

  return (
    <div>
      <MenuItem
        onClick={() => handleClickOnEntireRow()}
        className={classes.menuItem}
        classes={{ root: classes.menuItemRoot }}>
        <div
          className={[
            classes.tableRowRoot,
            hasDetails === true ? classes.tableRowRootWithCursor : ''
          ].join(' ')}>
          <div
            className={classes.tableRow}
            // component={LocalizedLink}
            // to={`/accommodations/${PropertyCode}/details`}
          >
            <div className={classes.nameCell}>
              <Typography className={classes.tableItem} variant="body1">
                {trackID && trackID.substring(0, 14)}...
              </Typography>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation()
                  onClickToCopy()
                }}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </div>
            <Hidden smDown>
              <div>
                <Typography className={classes.tableItem} variant="body1">
                  {lang === 'en'
                    ? creationDate
                    : gregorianToJalaali(creationDate)}
                </Typography>
              </div>
            </Hidden>
            <div>
              <StatusBox status={status} />
            </div>
            <div className={classes.iconContainer}>
              {status === 'InProgress' ? (
                <CircularProgress
                  className={classes.circularIcon}
                  size={20}
                  color="primary"
                />
              ) : (
                <IconButton onClick={(event) => handleDeleteJob(event)}>
                  <DeleteRoundedIcon />
                </IconButton>
              )}
            </div>
          </div>
        </div>
      </MenuItem>
      <JobDetailsModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        userRows={subjobs || []}
        ratePlanRows={subjobsForGenerateRatePlan || []}
      />
    </div>
  )
}

export default JobsRow
